'use client';

import { Badge, Box, Button, Flex, IconButton, Popover, Portal, Text } from '@chakra-ui/react';
import { Download } from 'lucide-react';
import NextLink from 'next/link';
import { useState } from 'react';
import { LoadingIndicator as Spinner } from '@/components/shared/LoadingIndicator';
import { DownloadsList } from './DownloadsList';
import { useDownloads } from './DownloadsProvider';

export function DownloadsMenu() {
  const { items, refresh } = useDownloads();
  const [open, setOpen] = useState(false);
  const activeCount = items.filter(
    item => item.status === 'preparing' || item.status === 'downloading'
  ).length;
  return (
    <Popover.Root
      open={open}
      positioning={{ placement: 'bottom-end', strategy: 'fixed' }}
      onOpenChange={({ open }) => {
        setOpen(open);
        if (open) refresh();
      }}
    >
      <Popover.Trigger asChild>
        <IconButton
          variant="ghost"
          boxSize="38px"
          minW="38px"
          flexShrink={0}
          borderRadius="12px"
          color="ui.text.secondary"
          position="relative"
          _icon={{ boxSize: '18px' }}
          _hover={{ bg: 'ui.surface.input', color: 'ui.text.primary' }}
          aria-label={`Downloads${activeCount ? `, ${activeCount} active` : ''}`}
          title="Downloads"
        >
          {activeCount ? <Spinner size="xs" /> : <Download size={18} strokeWidth={1.75} />}
          {activeCount > 0 && (
            <Badge
              position="absolute"
              top="0"
              right="0"
              size="xs"
              borderRadius="full"
              pointerEvents="none"
            >
              {activeCount}
            </Badge>
          )}
        </IconButton>
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            layerStyle="surfacePopover"
            width="400px"
            maxW="calc(100vw - 24px)"
            borderRadius="xl"
            overflow="hidden"
          >
            <Flex align="center" justify="space-between" px="4" py="3" borderBottomWidth="1px">
              <Popover.Title fontSize="sm" fontWeight="semibold">
                Project downloads
              </Popover.Title>
              <Popover.CloseTrigger asChild>
                <IconButton size="xs" variant="ghost" aria-label="Close downloads">
                  ×
                </IconButton>
              </Popover.CloseTrigger>
            </Flex>
            <Box maxH="360px" overflowY="auto">
              <DownloadsList compact />
            </Box>
            {activeCount > 0 && (
              <Text px="4" py="3" fontSize="xs" color="ui.text.secondary">
                You can keep working. Keep this tab open while exports finish.
              </Text>
            )}
            <Box px="4" py="3" borderTopWidth="1px">
              <Button size="sm" variant="ghost" asChild w="full">
                <NextLink href="/app/docs?tab=downloads" onClick={() => setOpen(false)}>
                  View all downloads
                </NextLink>
              </Button>
            </Box>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  );
}
